import React, { useContext, useEffect, useRef, useState } from 'react'
import { Content, ListContent, PageBreadcrumb, PageBreadcrumbPrimary, PageBreadcrumbSecondary, ButtonCancel, CreateContent } from './style'
import { LeftSide, RightSide, SectionName, SubHeaderContainer, Tab, TabsDiv, ButtonCreate, OptionsButton, FilterWrapper } from '../../../components/styles/subheader-style'
import orders from '../../../assets/fashion/TableFashionHeader/orders.svg'
import clock from '../../../assets/fashion/TableFashionHeader/clock.svg'
import diskIcon from '../../../assets/addStockModal/pages/disk.svg'
import { Outlet, useNavigate, useOutletContext, useLocation, useParams, useMatch, useSearchParams } from 'react-router-dom'
import { TypeMainOutlet } from '..'
import LoadingComponent from '../../../components/LoadingComponent'
import ModuleKanban from '../Pipelines/ModuleKanban'
import { Filter as FilterIcon } from 'lucide-react'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu'
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay'
import { Header, Filter } from '../../../components/SubHeaderFilter/types'

export const fashionOrdersHeaders: Header[] = [
  { key: 'id', label: 'Nº do pedido', type: 'number' },
  { key: 'client', label: 'Cliente', type: 'string' },
  { key: 'orderDate', label: 'Data pedido', type: 'date' },
  { key: 'startDateOfDelivery', label: 'Data entrega', type: 'date' },
  { key: 'quantityItems', label: 'Qtd. de itens', type: 'number' },
  { key: 'quantityPieces', label: 'Qtd. de peças', type: 'number' },
  { key: 'total', label: 'Total (R$)', type: 'currency' },
  {
    key: 'saleStatus',
    label: 'Venda',
    type: 'string',
    selectOptions: [
      { value: 'Em orçamento', label: 'Em orçamento' },
      { value: 'Fechado', label: 'Fechado' },
      { value: 'Cancelado', label: 'Cancelado' }
    ]
  }
]

interface ProductType {
  id: number
  name: string
  quantity: number
}

export interface FashionOutletType {
  tabIndex: number
  setTabIndex: React.Dispatch<React.SetStateAction<number>>
  addedProducts: ProductType[]
  setAddedProducts: React.Dispatch<React.SetStateAction<ProductType[]>>
  handleSave: React.MutableRefObject<() => void>
  handleEditSave: React.MutableRefObject<() => void>
  search?: string
  isSaving?: boolean
  setIsSaving?: React.Dispatch<React.SetStateAction<boolean>>
  appliedFilters: Filter[]
}

const tabs = [
  { path: '', image: orders, name: 'Pedidos' },
  { path: 'board', image: clock, name: 'Funil' },
]

const FashionSubHeader: React.FC<{
  tabIndexActived: number
  isCreating: boolean
  isEditing: boolean
  isBoardTab: boolean
  onSave?: () => void
  onEditSave?: () => void
  appliedFilters: Filter[]
  onApplyFilter: (f: Filter) => void
}> = ({ tabIndexActived, isCreating, isEditing, isBoardTab, onSave, onEditSave, appliedFilters, onApplyFilter }) => {
  const navigate = useNavigate()
  const { orderId } = useParams()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const filterButtonRef = useRef<HTMLDivElement | null>(null)

  return (
    <SubHeaderContainer>
      <LeftSide>
        <SectionName>
          {isCreating
            ? 'Novo pedido: 123456'
            : isEditing
            ? `Editar pedido: ${orderId}`
            : 'Pedidos fashion'}
        </SectionName>
        {!isCreating && !isEditing && (
          <TabsDiv>
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                $isFirst={index === 0}
                $isLast={index === tabs.length - 1}
                $isActive={index === tabIndexActived}
                onClick={() => {
                  if (index !== tabIndexActived) navigate(tab.path)
                }}
              >
                {tab.path === 'board' ? (
                  <FilterIcon size={12.8} style={{ marginRight: 7 }} />
                ) : (
                  <img src={tab.image} alt={`${tab.path}-icon`} />
                )}
                {tab.name}
              </Tab>
            ))}
          </TabsDiv>
        )}
      </LeftSide>
      <RightSide style={{ position: 'relative', alignItems: 'center' }}>
        {!isCreating && !isEditing && !isBoardTab && (
          <FilterWrapper>
            <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
              <FilterIcon size={18} />
            </OptionsButton>
            {isFilterMenuOpen && (
              <FilterMenu
                headers={fashionOrdersHeaders}
                onApplyFilter={(f) => {
                  onApplyFilter(f)
                  setIsFilterMenuOpen(false)
                }}
                onClose={() => setIsFilterMenuOpen(false)}
                anchorRef={filterButtonRef}
                filters={appliedFilters}
              />
            )}
          </FilterWrapper>
        )}

        {(isCreating || isEditing) && (
          <ButtonCancel
            onClick={() => navigate(returnTo || '/panel/fashion', { replace: true })}
          >
            Cancelar
          </ButtonCancel>
        )}

        {!isBoardTab && (
          <ButtonCreate
            onClick={async () => {
              try {
                if (isCreating && onSave) {
                  await onSave()
                  navigate(returnTo ? `${returnTo}` : '/panel/fashion', { replace: true })
                } else if (isEditing && onEditSave) {
                  await onEditSave()
                  navigate(returnTo ? `${returnTo}` : '/panel/fashion', { replace: true })
                } else {
                  navigate(`/panel/fashion/create${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`)
                }
              } catch (e) {
                console.warn('[FashionSubHeader] Salvamento impedido ou falhou:', e)
              }
            }}
          >
            {isCreating ? (
              <>
                <img src={diskIcon} alt="disk" />
                Salvar pedido
              </>
            ) : isEditing ? (
              <>
                <img src={diskIcon} alt="disk" />
                Atualizar pedido
              </>
            ) : (
              '+ Novo pedido'
            )}
          </ButtonCreate>
        )}
      </RightSide>
    </SubHeaderContainer>
  )
}

const FashionContent = () => {
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [addedProducts, setAddedProducts] = useState<ProductType[]>(() => {
    const saved = sessionStorage.getItem('addedProducts')
    return saved ? JSON.parse(saved) : []
  })

  const handleSave = useRef<() => void>(() => {})
  const handleEditSave = useRef<() => void>(() => {})
  const [isSaving, setIsSaving] = useState(false)

  const { search } = useOutletContext<TypeMainOutlet>()
  const location = useLocation()
  const navigate = useNavigate()
  const boardMatch = useMatch('/panel/fashion/board')

  const isCreating = location.pathname.includes('/panel/fashion/create')
  const isEditing = location.pathname.startsWith('/panel/fashion/edit/')
  const isBoardTab = !!boardMatch

  const fashionFiltersState = usePersistedFilters('fashion-orders')

  useEffect(() => {
    if (location.pathname === '/panel/fashion' || location.pathname === '/panel/fashion/') {
      setTabIndex(0)
    } else if (location.pathname.includes('/panel/fashion/board')) {
      setTabIndex(1)
    }
  }, [location.pathname])

  useEffect(() => {
    sessionStorage.setItem('addedProducts', JSON.stringify(addedProducts))
  }, [addedProducts])

  const outletContext: FashionOutletType = {
    tabIndex,
    setTabIndex,
    addedProducts,
    setAddedProducts,
    handleSave,
    handleEditSave,
    search,
    isSaving,
    setIsSaving,
    appliedFilters: fashionFiltersState.appliedFilters
  }

  if (isBoardTab) {
    return (
      <>
        {isSaving && <LoadingComponent />}
        <ModuleKanban
          moduleType="sales"
          moduleName="Vendas Fashion"
          subModule="fashion"
          headerLeftContent={
            <>
              <SectionName>Pedidos fashion</SectionName>
              <TabsDiv>
                {tabs.map((tab, index) => (
                  <Tab
                    key={index}
                    $isFirst={index === 0}
                    $isLast={index === tabs.length - 1}
                    $isActive={index === tabIndex}
                    onClick={() => {
                      if (index !== tabIndex) navigate(tab.path)
                    }}
                  >
                    {tab.path === 'board' ? (
                      <FilterIcon size={12.8} style={{ marginRight: 7 }} />
                    ) : (
                      <img src={tab.image} alt={`${tab.path}-icon`} />
                    )}
                    {tab.name}
                  </Tab>
                ))}
              </TabsDiv>
            </>
          }
        />
      </>
    )
  }

  return (
    <>
      {isSaving && <LoadingComponent />}
      <FashionSubHeader
        tabIndexActived={tabIndex}
        isCreating={isCreating}
        isEditing={isEditing}
        isBoardTab={isBoardTab}
        onSave={() => handleSave.current()}
        onEditSave={() => handleEditSave.current()}
        appliedFilters={fashionFiltersState.appliedFilters}
        onApplyFilter={fashionFiltersState.handleApplyFilter}
      />

      {!isCreating && !isEditing && fashionFiltersState.appliedFilters.length > 0 && (
        <AppliedFiltersDisplay
          filters={fashionFiltersState.appliedFilters}
          onRemoveFilter={fashionFiltersState.handleRemoveFilter}
          onClearAll={fashionFiltersState.handleClearAll}
        />
      )}

      {(isCreating || isEditing) && (
        <PageBreadcrumb>
          <PageBreadcrumbPrimary>Vendas</PageBreadcrumbPrimary>
          <PageBreadcrumbSecondary>&gt; {isCreating ? 'Novo pedido' : 'Editar pedido'}</PageBreadcrumbSecondary>
        </PageBreadcrumb>
      )}

      {(isCreating || isEditing) ? (
        <CreateContent>
          <Outlet context={outletContext} />
        </CreateContent>
      ) : (
        <ListContent>
          <Outlet context={outletContext} />
        </ListContent>
      )}
    </>
  )
}

export default FashionContent
